import React, { useContext } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AppContext from 'context/Context';
import {
  faUsers,
  faUserCheck,
  faChevronDown,
  faHeadset,
  faCheck,
  faTimes,
  faUser
} from '@fortawesome/free-solid-svg-icons';

const formatTitleCase = str => {
  if (!str) return '';
  return str
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const getDisplayName = (fullName, fallbackUser) => {
  if (!fullName) return fallbackUser || 'Usuario';
  const clean = fullName.trim();
  if (!clean) return fallbackUser || 'Usuario';

  const parts = clean.split(/\s+/).filter(Boolean);
  const toTitleCase = word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

  if (parts.length === 1) {
    return toTitleCase(parts[0]);
  }
  const firstName = toTitleCase(parts[0]);
  const surname = parts.length >= 3 ? toTitleCase(parts[2]) : toTitleCase(parts[1]);
  return `${firstName} ${surname}`;
};

const AgentFilterSelect = ({
  selectedAgentId,
  onSelectAgent,
  agents = [],
  currentUserId,
  currentUserName,
  isLoadingAgents = false,
  variant = 'default',
  className = ''
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const isMine = currentUserId && String(selectedAgentId) === String(currentUserId);
  const isAll = !selectedAgentId || selectedAgentId === '' || selectedAgentId === 'todos';

  // Obtener el nombre del asesor seleccionado para el modo 'card'
  const getSelectedDisplayName = () => {
    if (isMine) {
      return getDisplayName(currentUserName, 'Mis métricas');
    }
    if (isAll) {
      return 'Todos los asesores';
    }
    const found = agents.find(a => String(a.idUsuario) === String(selectedAgentId));
    if (found) {
      return formatTitleCase(found.vNombreCompleto || found.vNombre) || `Asesor #${found.idUsuario}`;
    }
    return 'Seleccionar asesor';
  };

  const badgeIcon = isMine ? faUserCheck : isAll ? faUsers : faHeadset;
  const badgeBg = isDark
    ? (isMine ? 'rgba(16, 185, 129, 0.2)' : 'rgba(14, 165, 233, 0.2)')
    : (isMine ? '#ecfdf5' : '#e0f2fe');
  const badgeColor = isDark
    ? (isMine ? '#34d399' : '#38bdf8')
    : (isMine ? '#059669' : '#0284c7');
  const subLabel = isMine ? 'Mis Métr cas' : isAll ? 'Vista General' : 'Asesor Asignado';

  const renderOptions = () => (
    <>
      {/* Opción por defecto: Todos los asesores */}
      <option value="">
        Todos los asesores
      </option>

      {/* Opción: Mis métricas (usuario que inició sesión) */}
      {currentUserId && (
        <option value={String(currentUserId)}>
          {getDisplayName(currentUserName)}
        </option>
      )}

      {/* Lista de demás asesores */}
      {agents
        .filter(a => String(a.idUsuario) !== String(currentUserId))
        .map(agent => {
          const name = formatTitleCase(agent.vNombreCompleto || agent.vNombre) || `Asesor #${agent.idUsuario}`;
          return (
            <option key={agent.idUsuario} value={String(agent.idUsuario)}>
              {name}
            </option>
          );
        })}
    </>
  );

  if (variant === 'chip') {
    const isSelected = !isAll;
    const selectedName = getSelectedDisplayName();

    return (
      <Dropdown className={`flex-shrink-0 ${className ? className : 'd-inline-block'}`}>
        <Dropdown.Toggle
          as="div"
          role="button"
          tabIndex={0}
          className="d-inline-flex align-items-center gap-1.5 px-2 px-sm-3 py-1 rounded-pill shadow-none dropdown-caret-none w-100 justify-content-between"
          style={{
            cursor: 'pointer',
            minWidth: 0,
            backgroundColor: isSelected
              ? (isDark ? 'rgba(14, 165, 233, 0.15)' : '#e0f2fe')
              : (isDark ? '#1e293b' : '#ffffff'),
            color: isSelected
              ? (isDark ? '#38bdf8' : '#0284c7')
              : (isDark ? '#cbd5e1' : '#334155'),
            border: isSelected
              ? (isDark ? '1px solid rgba(14, 165, 233, 0.35)' : '1px solid #bae6fd')
              : (isDark ? '1px solid #334155' : '1px solid #e2e8f0'),
            fontSize: '0.80rem',
            fontWeight: 500,
            whiteSpace: 'nowrap'
          }}
        >
          <div className="d-flex align-items-center gap-1.5 overflow-hidden flex-grow-1 min-w-0 me-1" style={{ minWidth: 0 }}>
            <span className="text-truncate">
              {selectedName}
            </span>
          </div>
          <div className="d-flex align-items-center flex-shrink-0 ms-auto">
            {isSelected && (
              <span
                role="button"
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  onSelectAgent('');
                }}
                className="me-1 d-inline-flex align-items-center justify-content-center"
                style={{
                  width: '16px',
                  height: '16px',
                  borderRadius: '50%',
                  color: isDark ? '#38bdf8' : '#0284c7',
                  cursor: 'pointer'
                }}
                title="Quitar filtro de asesor"
              >
                <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.68rem' }} />
              </span>
            )}
            <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: '0.65rem', opacity: 0.7 }} />
          </div>
        </Dropdown.Toggle>

        <Dropdown.Menu
          className={`shadow-lg border py-1 ${isDark ? 'bg-dark' : 'bg-white'}`}
          style={{
            minWidth: '220px',
            maxHeight: '300px',
            overflowY: 'auto',
            zIndex: 1050,
            borderColor: isDark ? '#334155' : '#e2e8f0'
          }}
        >
          <Dropdown.Item
            as="button"
            type="button"
            className={`se-agent-dropdown-item ${isAll ? 'se-item-selected-blue' : ''}`}
            onClick={() => onSelectAgent('')}
          >
            <div className="d-flex align-items-center gap-3 overflow-hidden flex-grow-1">
              <FontAwesomeIcon icon={faUsers} className="flex-shrink-0" style={{ width: '16px' }} />
              <span>Todos los asesores</span>
            </div>
            {isAll && <FontAwesomeIcon icon={faCheck} className="ms-auto" />}
          </Dropdown.Item>

          {currentUserId && (
            <Dropdown.Item
              as="button"
              type="button"
              className={`se-agent-dropdown-item ${isMine ? 'se-item-selected-blue' : ''}`}
              onClick={() => onSelectAgent(String(currentUserId))}
            >
              <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1">
                <FontAwesomeIcon icon={faUserCheck} className="flex-shrink-0" style={{ width: '16px' }} />
                <span>{getDisplayName(currentUserName)} (Mis métricas)</span>
              </div>
              {isMine && <FontAwesomeIcon icon={faCheck} className="ms-auto" />}
            </Dropdown.Item>
          )}

          {agents
            .filter(a => String(a.idUsuario) !== String(currentUserId))
            .map(agent => {
              const agentId = String(agent.idUsuario);
              const isSelectedAgent = String(selectedAgentId) === agentId;
              const agentName = formatTitleCase(agent.vNombreCompleto || agent.vNombre) || `Asesor #${agent.idUsuario}`;
              return (
                <Dropdown.Item
                  key={agent.idUsuario}
                  as="button"
                  type="button"
                  className={`se-agent-dropdown-item ${isSelectedAgent ? 'se-item-selected-blue' : ''}`}
                  onClick={() => onSelectAgent(agentId)}
                >
                  <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1">
                    <FontAwesomeIcon icon={faHeadset} className="flex-shrink-0" style={{ width: '16px' }} />
                    <span className="text-truncate">{agentName}</span>
                  </div>
                  {isSelectedAgent && <FontAwesomeIcon icon={faCheck} className="ms-auto" />}
                </Dropdown.Item>
              );
            })}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  if (variant === 'card') {
    return (
      <Dropdown className="w-100">
        <Dropdown.Toggle
          as="div"
          bsPrefix="se-agent-card-select"
          role="button"
          tabIndex={0}
        >
          <div className="d-flex align-items-center gap-2 gap-sm-3 overflow-hidden flex-grow-1" style={{ minWidth: 0 }}>
            <div
              className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
              style={{ width: '36px', height: '36px', backgroundColor: badgeBg, color: badgeColor }}
            >
              <FontAwesomeIcon icon={badgeIcon} style={{ fontSize: '1rem' }} />
            </div>
            <div className="overflow-hidden pe-1 text-start" style={{ minWidth: 0, flex: 1 }}>
              <div
                className="text-uppercase fw-bold text-truncate"
                style={{
                  fontSize: '0.62rem',
                  letterSpacing: '0.04em',
                  color: isMine ? (isDark ? '#34d399' : '#059669') : (isDark ? '#94a3b8' : '#64748b')
                }}
              >
                {subLabel}
              </div>
              <div className="fw-bold text-900 text-truncate" style={{ fontSize: '0.88rem' }}>
                {getSelectedDisplayName()}
              </div>
            </div>
          </div>

          <FontAwesomeIcon
            icon={faChevronDown}
            className="flex-shrink-0 ms-2 text-500"
            style={{ fontSize: '0.80rem' }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu className="se-agent-dropdown-menu shadow-lg border" style={{ minWidth: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
          {/* Opción 1: Todos los asesores */}
          <Dropdown.Item
            as="button"
            type="button"
            className={`se-agent-dropdown-item ${isAll ? 'se-item-selected-blue' : ''}`}
            onClick={() => onSelectAgent('')}
          >
            <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1 me-2" style={{ minWidth: 0 }}>
              <FontAwesomeIcon icon={faUsers} className="flex-shrink-0" style={{ color: isDark ? '#38bdf8' : '#0284c7', width: '16px' }} />
              <span className="text-truncate">Todos los asesores</span>
            </div>
            {isAll && <FontAwesomeIcon icon={faCheck} className="flex-shrink-0 ms-auto" style={{ color: isDark ? '#38bdf8' : '#0284c7' }} />}
          </Dropdown.Item>

          {/* Opción 2: Mis métricas (usuario logueado) */}
          {currentUserId && (
            <Dropdown.Item
              as="button"
              type="button"
              className={`se-agent-dropdown-item ${isMine ? 'se-item-selected-green' : ''}`}
              onClick={() => onSelectAgent(String(currentUserId))}
            >
              <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1 me-2" style={{ minWidth: 0 }}>
                <FontAwesomeIcon icon={faUserCheck} className="flex-shrink-0" style={{ color: isDark ? '#34d399' : '#059669', width: '16px' }} />
                <span className="text-truncate">
                  {getDisplayName(currentUserName)} (Mis métricas)
                </span>
              </div>
              {isMine && <FontAwesomeIcon icon={faCheck} className="flex-shrink-0 ms-auto" style={{ color: isDark ? '#34d399' : '#059669' }} />}
            </Dropdown.Item>
          )}

          {/* Lista de demás asesores */}
          {agents.length > 0 && <Dropdown.Divider className="my-1" />}
          {agents.length > 0 && (
            <Dropdown.Header className="px-3 py-1 text-uppercase text-500" style={{ fontSize: '0.65rem', fontWeight: 700 }}>
              Equipo de Soporte
            </Dropdown.Header>
          )}

          {isLoadingAgents && agents.length === 0 ? (
            <div className="text-center py-2 text-500" style={{ fontSize: '0.80rem' }}>
              <Spinner animation="border" size="sm" className="me-2" />
              Cargando asesores...
            </div>
          ) : (
            agents
              .filter(a => String(a.idUsuario) !== String(currentUserId))
              .map(agent => {
                const agentId = String(agent.idUsuario);
                const isSelected = String(selectedAgentId) === agentId;
                const agentName = formatTitleCase(agent.vNombreCompleto || agent.vNombre) || `Asesor #${agent.idUsuario}`;
                return (
                  <Dropdown.Item
                    key={agent.idUsuario}
                    as="button"
                    type="button"
                    className={`se-agent-dropdown-item ${isSelected ? 'se-item-selected-blue' : ''}`}
                    onClick={() => onSelectAgent(agentId)}
                  >
                    <div className="d-flex align-items-center gap-2 overflow-hidden flex-grow-1 me-2" style={{ minWidth: 0 }}>
                      <FontAwesomeIcon
                        icon={faHeadset}
                        className="flex-shrink-0"
                        style={{
                          color: isSelected ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#94a3b8' : '#64748b'),
                          width: '16px'
                        }}
                      />
                      <span className="text-truncate">{agentName}</span>
                    </div>
                    {isSelected && (
                      <FontAwesomeIcon
                        icon={faCheck}
                        className="flex-shrink-0 ms-auto"
                        style={{ color: isDark ? '#38bdf8' : '#0284c7' }}
                      />
                    )}
                  </Dropdown.Item>
                );
              })
          )}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  return (
    <div className="d-flex align-items-center gap-2 se-agent-filter-container w-100 w-sm-auto" style={{ minWidth: 0 }}>
      <label
        htmlFor="tickets-agent-filter-select"
        className="d-flex align-items-center gap-1 text-600 mb-0 fw-semibold flex-shrink-0"
        style={{ fontSize: '0.80rem', whiteSpace: 'nowrap' }}
      >
        <FontAwesomeIcon
          icon={faUserCheck}
          style={{ color: isDark ? '#34d399' : '#059669' }}
        />
        <span>Asesor:</span>
      </label>

      <select
        id="tickets-agent-filter-select"
        className="form-select form-select-sm border shadow-none text-900 fw-semibold se-agent-filter-select"
        style={{
          fontSize: '0.82rem',
          borderRadius: '8px',
          cursor: 'pointer',
          minWidth: 0,
          flex: 1
        }}
        value={isAll ? '' : String(selectedAgentId)}
        onChange={e => onSelectAgent(e.target.value)}
        title="Filtrar métricas por asesor asignado"
        disabled={isLoadingAgents && agents.length === 0}
      >
        {renderOptions()}
      </select>
    </div>
  );
};

export default AgentFilterSelect;
